
/**
 * Studio — the create page workbench (2026-11-02 truth; older acts deleted):
 *
 *   Intent bar — material tile + 4 recommended style intents (hover shows the
 *                成品图 composed on the active carrier); tap a style to swap
 *                it via the picker modal. The prompt bar carries the source
 *                attach button, free text, vision "pieces" (the photo IS the
 *                prompt — DEFAULT_ON categories start checked) and voice input.
 *   Board     — one 2x2 board IS one FLUX-3 sheet (FILM_SIZE = SHEET_BATCH = 4):
 *                one click fills the whole board; identical inputs hit the
 *                worker's 24h idempotency cache ($0).
 *   Preview   — tile tap opens the preview modal: material + mockup composed
 *                on the active blank carrier (chips switch free, client-side),
 *                single-cell refine ($0.048 full-canvas), Use as material
 *                (C→A loop-back), Use this design (→ Design mode with the
 *                upscaled art).
 *   Buy       — one-click buy (2026-11-02): Buy → server lane
 *                (POST /api/customer/create/buy: SDS produce → customer-owned
 *                design version → Shopify publish intent) → the modal ends on
 *                the real PDP link. Price comes from the carrier's governed
 *                product via /api/blanks — nothing is hardcoded.
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { CONFIG } from '../../core/config.js'
import { apiRequest, uploadImage } from '../../api/index.js'
import { setTier, getTier, TIERS } from './state.js'
import { wallItems } from '../create-ui/style-seeds.js'
import { composeMockup, getBlankKey, setBlankKey, loadBlanks, getActiveBuyable } from '../create-ui/mockup-composite-tab.js'
import { useImageAsSource, materializeExternalSource } from '../create-ui/image-roles.js'
import { DEFAULT_ON } from '../create/cards/state.js'
import { generateSheet, generateCell, startBuy, waitForBuy } from '../../api/create-sheet.js'
import { requireAuth } from '../auth/index.js'
import { upscaleImage } from '../../api/ai-tools.js'
import { setMode } from '../../core/router.js'
import { writeRefAttribution } from '../../utils/ref-attribution.js'
import { loadGallery } from '../gallery/index.js'
import { showToast } from '../../utils/toast.js'
import { createLogger } from '../../utils/logger.js'

const log = createLogger('studio')

const L0_DEFAULT_INTENT = {
  id: null,
  execution_mode: 'ideogram',
  input_mode: 'text',
  title: 'Text to Image',
  requires_second_image: 0,
}

// Price is never hardcoded (2026-11-02 production ruling): the buy button
// reads the active carrier's governed price from /api/blanks (worker joins
// productsDb). buyable:false carriers show preview-only.
const FILM_SIZE = 4
// The style pool is larger than one batch so the shuffle button can rotate it (cell-by-cell refresh).
const STYLE_POOL_MIN = 13
// Sheet flow (bao 2026-10-29 2x2@1024 ruling, replaces the 3x3@1536 sheet):
// ONE bfl-flux-3-image 2x2 sheet per generate click ($0.048, ~45s), sliced
// + white-keyed server-side into 4 style tiles. The intent board IS one
// sheet (FILM_SIZE = SHEET_BATCH = 4): one click fills the whole board.
// Style ids ride along so tile slot i keeps the intent grid's style
// alignment (locks/shuffles intact). Identical inputs within 24h are served
// from the worker's idempotency cache ($0).
const SHEET_BATCH = 4

// Set when a studio generate was blocked by the login gate, so it auto-resumes
// after sign-in (same _pendingGenerate pattern as create/index.js).
let _pendingStudioGenerate = false
// One error toast per batch, not four (a shared quota/429 failure fails every cell).
let _lastErrorToastToken = 0

// Phosphor Icons (linear SVG)
const studio = {
  root: null,
  initialized: false,
  listenersBound: false,
  act: 'hero', // 'hero' | 'gallery'
  // The nine-grid adapter (completeCell/failCell/resetCell over cell phases).
  materialGrid: null,
  stylePool: [],
  batchOffset: 0,
  popstateHandler: null,
  // Track locked intent cells
  lockedIntentCellIds: new Set(),
  revealed: false,
  // Sheet flow (2026-10-23): free-text prompt input (two-mode ruling — a
  // generate is legal with a prompt even without a source image).
  prompt: '',
  // Prompt pieces read from the source image (VISION_COMPLETE) — the photo IS
  // the prompt: each card toggles on/off and checked pieces join the sheet
  // prompt (create-page-demo v3, 2026-10-24). No typing required.
  pieces: [],
  piecesLoading: false,
  generating: false,
  genToken: 0,
  // Style ids dispatched by the in-flight sheet (progress denominator).
  sheetDispatch: [],
  // Preview modal state
  previewModalOpen: false,
  previewModalItem: null,
  // Right panel overlay state
  rpOverlayOpen: false,
  // Library modal: selected card (committed by the footer's Generate)
  libraryPickId: null,
  // eventBus/store unsubscribe fns from bindGlobalListeners (deactivate runs
  // them — the re-activate listener-leak guard).
  _unsubs: [],
  // Intent strip picker: which slot's candidate wall is open (null = closed).
  intentPickerSlot: null,
}

function esc(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

// ---- Shell + acts --------------------------------------------------------

function shellHtml() {
  // No in-shell topnav: the outer page-frame owns brand + navigation — the
  // old studio-topnav duplicated both as a dead decorative row.
  return `<div class="studio-shell" data-studio-root>
    <main class="studio-main"><div class="studio-stage" data-studio-stage></div></main>
    <div class="sg-sources" data-studio-sources hidden></div>
  </div>`
}

// Demo v4 layout (approved 2026-10-24): ONE input bar as the only interactive
// object + the results grid as the page body. The three-column workbench
// (source / intent / mockup columns, film-strip + reveal-grid) is retired —
// the grid merges IN (style labels) and OUT (generated tiles) in place
// (2026-10-29: four in four out — one board = one 2x2 sheet). Mockup/product
// preview lives in the tile preview modal (Batch B takes it further).
function galleryHtml() {
  return `<section class="studio-gallery" data-act="gallery">
    <!-- THE input bar -->
    <div class="sg-bar">
      <button class="sg-attach" type="button" data-studio-action="toggle-library" title="Pick from library" data-sg-attach>
        <span class="sg-attach-icon" data-sg-attach-icon>＋</span>
        <span class="sg-attach-clear" data-studio-action="clear-source" title="Remove image">×</span>
      </button>
      <input class="sg-bar-prompt" type="text" data-sg-design-prompt
        placeholder="Pick an image from your library, or describe one…" autocomplete="off"
        aria-label="Design prompt">
      <button class="sg-bar-shuffle" type="button" data-studio-action="shuffle">↻ Shuffle styles</button>
      <button class="sg-bar-go" type="button" data-studio-action="generate">Generate 4</button>
    </div>

    <!-- Prompt pieces read from the photo (the photo IS the prompt) -->
    <div class="sg-pieces-row" aria-label="Prompt pieces" data-sg-pieces-section hidden>
      <span class="sg-pieces-label">From your photo</span>
      <div class="sg-pieces" data-sg-pieces></div>
    </div>
    <div class="sg-prompt-preview" data-sg-prompt-preview hidden></div>

    <!-- Library as a MODAL (2026-10-27, bao): the inline panel pushed the
         page down; the layout must not move when the library opens. -->
    <div class="sg-intent-modal" data-sg-library hidden>
      <div class="sg-intent-modal-backdrop" data-studio-action="close-library"></div>
      <div class="sg-intent-modal-content sg-library-content">
        <div class="sg-intent-modal-head">
          <span>Your library</span>
          <button type="button" class="sg-intent-modal-close" data-studio-action="close-library" aria-label="Close">×</button>
        </div>
        <div class="sg-library-grid" data-sg-library-grid></div>
        <!-- Fixed generate area (2026-10-27, bao): pinned footer — pick a
             card, the generate action stays put instead of the layout moving. -->
        <div class="sg-library-foot">
          <span class="sg-library-foot-hint" data-sg-library-hint>Pick a material</span>
          <button type="button" class="sg-bar-go" data-studio-action="library-generate" data-sg-library-go disabled>Generate 4</button>
        </div>
      </div>
    </div>

    <!-- Stage 1 · A + B: the user's MATERIAL tile (A) heads the row, then
         the four recommended intents (B). Hover a B shows its 成品图; tap
         swaps it. The equation reads left to right: A + B → C below. -->
    <div class="sg-intents">
      <div class="sg-intents-head">
        <span class="sg-intents-title">Your image + styles we'll use</span>
        <span class="sg-intents-tip">tap a style to swap it</span>
      </div>
      <div class="sg-intents-row">
        <button type="button" class="sg-material-tile" data-sg-material data-studio-action="material-tap"
          title="Your material — picked in the bar above">
          <img class="sg-material-img" data-sg-material-img alt="Your material" hidden>
          <span class="sg-material-clear" data-studio-action="clear-source" title="Remove image" hidden>×</span>
        </button>
        <span class="sg-intents-plus" aria-hidden="true">+</span>
        <div class="sg-intent-strip" data-sg-intent-strip></div>
      </div>
    </div>

    <!-- Swap picker as a MODAL (2026-10-25, bao): the inline wall pushed the
         stage down and read as "the 成品图 are gone". A modal keeps the strip
         and results anchored, focuses the choice, and closes explicitly. -->
    <div class="sg-intent-modal" data-sg-intent-picker hidden>
      <div class="sg-intent-modal-backdrop" data-sg-intent-close></div>
      <div class="sg-intent-modal-content">
        <div class="sg-intent-modal-head">
          <span>Swap this intent</span>
          <button type="button" class="sg-intent-modal-close" data-sg-intent-close aria-label="Close">×</button>
        </div>
        <div class="sg-intent-modal-grid" data-sg-intent-picker-grid></div>
      </div>
    </div>

    <!-- Stage 2 · results grid: hidden until the first paid batch runs;
         cells only ever show rendering / failed / final art. -->
    <div class="sg-stage" data-sg-stage hidden>
      <div class="sg-stage-hint" data-sg-stage-hint></div>
      <div class="sg-nine" data-sg-nine></div>
    </div>

    <!-- Guided empty state: generate with no input points at the attach slot
         instead of a dead disabled key (Kittl "asks before it creates"). -->
    <div class="sg-guide-bubble" data-sg-guide-bubble>Start here — add your photo first</div>
    <!-- Preview Modal (material + mockup + the commitment actions) -->
    <div class="sg-preview-modal" data-sg-preview-modal hidden>
      <div class="sg-preview-modal-backdrop" data-sg-preview-close></div>
      <div class="sg-preview-modal-content">
        <button class="sg-preview-modal-close" type="button" data-sg-preview-close aria-label="Close preview">×</button>
        <div class="sg-preview-modal-images">
          <div class="sg-preview-modal-image">
            <div class="sg-preview-modal-label">Material</div>
            <img class="sg-preview-modal-img" data-sg-preview-material alt="Material" />
          </div>
          <div class="sg-preview-modal-image">
            <div class="sg-preview-modal-label">Mockup</div>
            <img class="sg-preview-modal-img" data-sg-preview-mockup alt="Mockup" />
          </div>
        </div>
        <!-- Batch B: product preview — switch the carrier, the mockup
             recomposes client-side (no paid call). -->
        <div class="sg-preview-blanks" data-sg-preview-blanks></div>
        <!-- P3 single-cell refine: re-render just this style with an edited
             prompt ($0.048 full-canvas render — one deliberate action, not a
             batch). -->
        <div class="sg-refine">
          <textarea class="sg-refine-prompt" data-sg-refine-prompt rows="2"
            placeholder="Tweak the prompt, re-render only this style"></textarea>
          <button class="sg-ghost sg-refine-btn" type="button" data-studio-action="refine-cell">Refine this style · $0.05</button>
        </div>
        <div class="sg-preview-modal-actions">
          <button class="sg-ghost sg-use-material" type="button" data-studio-action="use-material">↺ Use as material</button>
          <button class="sg-ghost sg-buy" type="button" data-studio-action="buy" data-sg-buy>Buy</button>
          <button class="sg-use-design" type="button" data-studio-action="use-design">Use this design →</button>
        </div>
        <div class="sg-buy-status" data-sg-buy-status hidden></div>
      </div>
    </div>
  </section>`
}

// ---- left column · source ------------------------------------------------

/**
 * Prompt pieces — the vision read of the source rendered as toggle cards
 * (create-page-demo v3: the photo IS the prompt; no typing). DEFAULT_ON
 * categories (color/lighting) start checked, matching the legacy workspace.
 */
function renderPieces() {
  const section = studio.root?.querySelector('[data-sg-pieces-section]')
  const host = studio.root?.querySelector('[data-sg-pieces]')
  if (!section || !host) return
  const hasSource = Boolean(store.getState('sourceImage')?.url)
  if (!hasSource || (!studio.piecesLoading && !studio.pieces.length)) {
    section.hidden = true
    return
  }
  section.hidden = false
  if (studio.piecesLoading) {
    host.innerHTML = `<div class="sg-pieces-reading">Reading your photo…</div>`
    return
  }
  host.innerHTML = studio.pieces
    .map(
      (p, i) => `<button class="sg-piece${p.on ? ' on' : ''}" type="button" data-piece-index="${i}" aria-pressed="${p.on}">
      <span class="sg-piece-ck" aria-hidden="true">${p.on ? '✓' : ''}</span>
      <span class="sg-piece-lbl"><span class="sg-piece-cat">${esc(p.category)}</span>${esc(p.title)}</span>
    </button>`
    )
    .join('')
}

function togglePiece(index) {
  const piece = studio.pieces[index]
  if (!piece) return
  piece.on = !piece.on
  renderPieces()
  renderPromptPreview()
}

/**
 * Live prompt preview (demo v4): the sentence that will be sent, rewritten
 * visibly as chips toggle or text is typed. Shown whenever a source or a
 * prompt exists; empty states explain the photo alone is enough.
 */
function renderPromptPreview() {
  const host = studio.root?.querySelector('[data-sg-prompt-preview]')
  if (!host) return
  const hasSource = Boolean(store.getState('sourceImage')?.url)
  const typed = studio.prompt?.trim()
  if (!hasSource && !typed) {
    host.hidden = true
    return
  }
  const parts = studio.pieces.filter((p) => p.on).map((p) => p.description || p.title).filter(Boolean)
  let text = parts.map(esc).join(' · ')
  if (typed) text = `<span class="typed">${esc(typed)}</span>` + (text ? ' · ' + text : '')
  host.innerHTML = text
    ? `<span class="pp-label">Prompt</span><span class="pp-text">${text}</span>`
    : `<span class="pp-label">Prompt</span><span class="pp-text pp-empty">the image alone is enough — or keep some pieces</span>`
  host.hidden = false
}

/**
 * The sheet prompt = the typed note + every CHECKED piece's fragment. Pieces
 * carry the vision-extracted description (the prompt fragment), falling back
 * to the title. why → the sheet endpoint takes a single `prompt` subject
 * string; the checked pieces are how the photo's read steers the batch.
 */
export function composeSheetPrompt() {
  const parts = []
  const typed = studio.prompt?.trim()
  if (typed) parts.push(typed)
  for (const p of studio.pieces) {
    if (p.on && (p.description || p.title)) parts.push(p.description || p.title)
  }
  return parts.join('. ')
}

/**
 * Guided empty state (Kittl "asks before it creates", demo v3): a generate
 * click with neither source nor prompt pulses the source column and shows the
 * pointer bubble — never a silent dead key, never a toast.
 */
function guideSourcePick() {
  const attach = studio.root?.querySelector('[data-sg-attach]')
  const bubble = studio.root?.querySelector('[data-sg-guide-bubble]')
  attach?.classList.add('sg-guide-pulse')
  bubble?.classList.add('on')
  clearTimeout(studio._guideTimer)
  studio._guideTimer = setTimeout(() => {
    attach?.classList.remove('sg-guide-pulse')
    bubble?.classList.remove('on')
  }, 3200)
}

/** Source band: the bar's attach slot — icon when empty, thumb + hover × when set. */
function renderSourceBand() {
  const attach = studio.root?.querySelector('[data-sg-attach]')
  const src = store.getState('sourceImage')
  if (attach) {
    const iconHost = attach.querySelector('[data-sg-attach-icon]')
    attach.querySelector('img')?.remove()
    if (!src?.url) {
      attach.classList.remove('has-img')
      if (iconHost) iconHost.hidden = false
    } else {
      attach.classList.add('has-img')
      if (iconHost) iconHost.hidden = true
      const img = document.createElement('img')
      img.src = src.url
      img.alt = src.name || src.title || 'Source image'
      attach.prepend(img)
    }
  }
  // The A tile (素材) at the head of the intent row mirrors the source.
  const tile = studio.root?.querySelector('[data-sg-material]')
  if (tile) {
    const img = tile.querySelector('[data-sg-material-img]')
    const clear = tile.querySelector('.sg-material-clear')
    const has = Boolean(src?.url)
    if (img) {
      img.hidden = !has
      if (has) img.src = src.url
    }
    if (clear) clear.hidden = !has
    tile.classList.toggle('has-img', has)
  }
  updatePhase()
}

/** The × on the attach slot — a cleared source re-locks the generate gate. */
function clearSource() {
  store.setState('sourceImage', null)
  renderSourceBand()
  updatePhase()
}

// ---- middle column · intent ------------------------------------------------

// ---- THE results grid: four in (compact labels), four out (full art) ------
// Cell phases derive from cell state, never stored separately:
//   'out'       — cell.generated && cell.imageUrl (the tile landed)
//   'failed'    — cell.failure set by the sheet flow (tap to retry)
//   'rendering' — a batch is in flight and this cell has no tile yet
//   'idle'      — no paid batch yet; the results grid stays hidden
function cellPhase(cell) {
  if (cell.generated && cell.imageUrl) return 'out'
  if (cell.failure) return 'failed'
  if (studio.generating) return 'rendering'
  return 'idle'
}

/**
 * Intent thumbs (2026-10-25): the resting face is the style's own sample —
 * nine DISTINCT arts, the image IS the intent. Hover/focus crossfades to the
 * 成品图: that design composed onto the current product (成品 = 商品+素材,
 * client-side composeMockup, follows the preview modal's carrier pick).
 * Composites are cached per style+blank.
 */
/** Shared thumb visual: style sample at rest, 成品图 (mockup) on hover. The
 *  after img gets its src asynchronously from composeIntentAfters(). */
function intentVisual(cell, imgClass) {
  const raw = cell.sample_image_url || cell.imageUrl
  // placehold.co name plates are NOT samples (2026-10-27, bao): a style
  // without a real sample renders the standard shimmer, Pinterest-style —
  // never a colored name block.
  const img = raw && !raw.includes('placehold.co') ? raw : null
  if (!img) {
    return `<span class="sg-intent-sw sg-skel"></span>`
  }
  return `<img class="sg-intent-static" src="${esc(img)}" alt="${esc(cell.label)}" loading="lazy">
    <img class="${imgClass}" data-sg-after="${esc(cell.id)}" alt="" aria-hidden="true" loading="lazy">`
}

/**
 * Fill every [data-sg-after] img with the composed 成品图 (style sample on
 * the current blank). Cache holds PROMISES so concurrent renders share one
 * compose; failures leave the after empty (thumb just stays a before).
 */
async function composeIntentAfters() {
  const host = studio.root
  if (!host) return
  const blank = getBlankKey()
  studio.intentMockups = studio.intentMockups || new Map()
  await Promise.all(
    Array.from(host.querySelectorAll('[data-sg-after]')).map(async (img) => {
      const styleId = String(img.dataset.sgAfter)
      const cell = studio.stylePool.find((s) => String(s.id) === styleId)
      const sample = cell?.sample_image_url || cell?.imageUrl
      if (!sample) return
      const cacheKey = `${styleId}:${blank}`
      let pending = studio.intentMockups.get(cacheKey)
      if (!pending) {
        // Thumb mode: crop to the design area — legible at strip size.
        pending = composeMockup(sample, blank, { cropToMaterial: true })
          .then((r) => r.dataUrl)
          .catch(() => null)
        studio.intentMockups.set(cacheKey, pending)
        // Hard cap: 15 styles x 4 blanks of canvas dataURLs is plenty.
        if (studio.intentMockups.size > 100) studio.intentMockups.clear()
      }
      const dataUrl = await pending
      if (dataUrl && img.isConnected && String(img.dataset.sgAfter) === styleId) img.src = dataUrl
    })
  )
}

/**
 * Stage 1 · the intent strip. Nine small thumbs — the base image IS the
 * intent (no labels; the name is only a tooltip). A thumb the user swapped
 * in themselves wears .sel + ✓ and is implicit-locked against Shuffle.
 */
function renderIntentStrip() {
  const host = studio.root?.querySelector('[data-sg-intent-strip]')
  if (!host) return
  host.innerHTML = currentBatch()
    .map((cell, i) => {
      const picked = studio.lockedIntentCellIds.has(String(cell.id))
      return `<button type="button" class="sg-intent-thumb${picked ? ' sel' : ''}" data-sg-intent-slot="${i}" data-sg-intent-id="${esc(cell.id)}"
        title="${esc(cell.label)} — tap to swap" aria-pressed="${picked}">
        ${intentVisual(cell, 'sg-intent-after')}
        ${picked ? '<span class="sg-intent-badge" aria-hidden="true">✓</span>' : ''}
      </button>`
    })
    .join('')
  composeIntentAfters()
}

/**
 * Candidate wall for one slot, as a modal: every pool style not currently
 * on the board. Tap a candidate → it takes the slot (swapIntent), the modal
 * closes. Backdrop / × closes without changing anything.
 */
function renderIntentPicker() {
  const modal = studio.root?.querySelector('[data-sg-intent-picker]')
  const host = studio.root?.querySelector('[data-sg-intent-picker-grid]')
  if (!modal || !host) return
  if (studio.intentPickerSlot == null) {
    modal.hidden = true
    host.innerHTML = ''
    return
  }
  const onBoard = new Set(currentBatch().map((c) => String(c.id)))
  const candidates = studio.stylePool.filter((s) => !onBoard.has(String(s.id)))
  modal.hidden = false
  host.innerHTML = candidates.length
    ? candidates
        .map(
          (s) =>
            `<button type="button" class="sg-intent-cand" data-sg-intent-pick="${esc(s.id)}" title="${esc(s.label)}">${intentVisual(s, 'sg-intent-after')}</button>`
        )
        .join('')
    : '<div class="sg-intent-picker-empty">No more styles in the pool yet</div>'
  composeIntentAfters()
}

function toggleIntentPicker(slotIndex) {
  studio.intentPickerSlot = studio.intentPickerSlot === slotIndex ? null : slotIndex
  renderIntentPicker()
}

/**
 * Swap one slot for a pool style (the image the user picked IS the intent).
 * Works by splicing the style into the slot's pool position — currentBatch()
 * derives from pool+offset, so no parallel batch state can drift. The pick is
 * an implicit shuffle-lock and counts as a selection (H-127 telemetry).
 * Post-generate, only the swapped cell's paid result resets — the other 8
 * stay bought.
 */
function swapIntent(slotIndex, styleId) {
  const pool = studio.stylePool
  if (!pool.length) return
  const offset = studio.batchOffset % pool.length
  const slotPoolIdx = (offset + slotIndex) % pool.length
  const fromIdx = pool.findIndex((s) => String(s.id) === String(styleId))
  if (fromIdx < 0 || fromIdx === slotPoolIdx) return
  const [style] = pool.splice(fromIdx, 1)
  pool.splice(fromIdx < slotPoolIdx ? slotPoolIdx - 1 : slotPoolIdx, 0, style)
  studio.lockedIntentCellIds.add(String(style.id))
  studio.intentPickerSlot = null
  resetBatchCells([currentBatch()[slotIndex]])
  reportStyleSelect(style.id)
  renderIntentStrip()
  renderIntentPicker()
  renderResults()
}

/**
 * Stage 2 · results grid. Hidden until the first paid batch runs; cells only
 * ever render skel / failure / final art — the intent-strip owns selection.
 */
function renderResults() {
  const stage = studio.root?.querySelector('[data-sg-stage]')
  const host = studio.root?.querySelector('[data-sg-nine]')
  if (!stage || !host) return
  const batch = currentBatch()
  const live = studio.generating || batch.some((c) => (c.generated && c.imageUrl) || c.failure)
  stage.hidden = !live
  if (!live) {
    host.innerHTML = ''
    return
  }
  host.innerHTML = batch
    .map((cell) => {
      const phase = cellPhase(cell)
      let inner = ''
      if (phase === 'rendering') {
        inner = `<div class="skel"></div><div class="scan">Rendering ${esc(cell.label)}…</div>`
      } else if (phase === 'failed') {
        inner = `<div class="fail" data-nine-retry="${esc(cell.id)}">
          <span class="fail-msg">${esc(cell.failure || 'Generation failed')}</span>
          <span class="fail-retry">Tap to retry</span>
        </div>`
      } else if (phase === 'out') {
        // The made-by chip (hover): which B intent made this tile — the
        // equation A+B→C stays readable from the result side.
        const madeBy = cell.sample_image_url
          ? `<div class="madeby"><img src="${esc(cell.sample_image_url)}" alt="" loading="lazy"><span>${esc(cell.label)}</span></div>`
          : `<div class="madeby"><span>${esc(cell.label)}</span></div>`
        inner = `<div class="out" data-nine-view="${esc(cell.id)}">
          <img src="${esc(cell.imageUrl)}" alt="${esc(cell.label)}">
          <div class="tag">${esc(cell.label)}</div>
          ${madeBy}
        </div>`
      }
      return `<div class="nine-cell${phase === 'out' ? ' done' : ''}" data-nine-cell="${esc(cell.id)}">${inner}</div>`
    })
    .join('')
}

/**
 * Grid adapter — the sheet flow (runSheetGenerate / retryGenerateCell) speaks
 * completeCell/failCell/resetCell; the nine-grid maps those to cell phases.
 * Kept on studio.materialGrid so the generate gate's readiness check and the
 * retry path keep their existing contracts.
 */
function makeNineGridAdapter() {
  return {
    async completeCell(id) {
      const cell = currentBatch().find((c) => String(c.id) === String(id))
      if (cell) cell.failure = null
      renderResults()
      updateNineProgress()
    },
    failCell(id, err, message) {
      const cell = currentBatch().find((c) => String(c.id) === String(id))
      if (cell) cell.failure = message || 'Generation failed'
      renderResults()
      updateNineProgress()
    },
    resetCell(id) {
      const cell = currentBatch().find((c) => String(c.id) === String(id))
      if (cell) cell.failure = null
      renderResults()
    },
  }
}

/**
 * Warm the 成品图 cache for the WHOLE pool in the background. Picker
 * candidates are off-board styles — without this their hover face is empty
 * for the first seconds after the wall opens.
 */
function warmIntentMockups() {
  setTimeout(() => {
    studio.intentMockups = studio.intentMockups || new Map()
    const blank = getBlankKey()
    for (const s of studio.stylePool) {
      const sample = s.sample_image_url || s.imageUrl
      if (!sample) continue
      const cacheKey = `${String(s.id)}:${blank}`
      if (studio.intentMockups.has(cacheKey)) continue
      const pending = composeMockup(sample, blank, { cropToMaterial: true })
        .then((r) => r.dataUrl)
        .catch(() => null)
      studio.intentMockups.set(cacheKey, pending)
    }
  }, 300)
}

/** Generate-button progress: "Rendering… n/4" over the dispatched cells. */
function updateNineProgress() {
  const btn = studio.root?.querySelector('[data-studio-action="generate"]')
  if (!btn || !studio.generating) return
  const batch = currentBatch()
  const dispatched = studio.sheetDispatch?.length
    ? batch.filter((c) => studio.sheetDispatch.includes(String(c.id)))
    : batch
  const settled = dispatched.filter((c) => (c.generated && c.imageUrl) || c.failure).length
  btn.textContent = `Rendering… ${settled}/${dispatched.length || SHEET_BATCH}`
}

/** Resting generate-key label: the next paid batch's size (0 left = free settle). */
function generateLabel() {
  const left = currentBatch().filter((c) => !(c.generated && c.imageUrl)).length
  return left ? `Generate ${Math.min(SHEET_BATCH, left)}` : 'Generate'
}

/** Reset per-cell paid results — a remount is a NEW paid-batch contract
 *  (shuffle / fresh pool): stale imageUrls must not suppress the fresh
 *  dispatch (H-163). */
function resetBatchCells(batch) {
  batch.forEach((cell) => {
    cell.generated = false
    cell.imageUrl = undefined
    cell.assetId = null
    cell.failure = null
    cell.retryInFlight = false
    cell.refining = false
    cell.selectReported = false
  })
}

function mountGrids(batch) {
  studio.genToken += 1
  resetBatchCells(batch)
  studio.materialGrid = makeNineGridAdapter()
  studio.intentPickerSlot = null
  // A remount can follow a pool refresh (seed → API): sample URLs may have
  // changed, so the composed 成品图 cache must not carry stale sources.
  studio.intentMockups = new Map()
  renderIntentStrip()
  renderIntentPicker()
  renderResults()
}

/**
 * Topbar auth chip — the only login entry/status on the create page.
 * Guest: "Sign in" opens the login modal. Signed in: avatar dot + display
 * name; clicking jumps to the Me page (account home).
 */
/**
 * One header row (bao 2026-10-24): the studio topbar is gone — its actions
 * live in the shared frame header's #stageActions slot, scoped to create
 * mode via data-stage-mode (the applyPageFrame pattern). Idempotent; binds
 * listeners directly since the buttons sit outside studio.root's delegated
 * click surface. Demo v4: shuffle/generate moved INTO the bar — the header
 * keeps only Inspiration + auth.
 */
function ensureStudioStageActions() {
  const host = document.getElementById('stageActions')
  if (!host || document.getElementById('sgAuthChip')) return
  const wrap = document.createElement('div')
  wrap.className = 'sg-stage-actions'
  wrap.dataset.stageMode = 'create'
  wrap.innerHTML = `
    <button class="sg-mini" type="button" data-rp-reopen title="Open Inspiration panel"><i class="ph ph-lightbulb" aria-hidden="true"></i> Inspiration</button>
    <button class="sg-mini sg-auth-chip" id="sgAuthChip" type="button" data-studio-action="auth"></button>`
  host.prepend(wrap)
  wrap.addEventListener('click', (event) => {
    if (event.target.closest('[data-rp-reopen]')) {
      toggleRpOverlay(!studio.rpOverlayOpen)
      return
    }
    const el = event.target.closest('[data-studio-action]')
    if (!el) return
    if (el.dataset.studioAction === 'auth') {
      if (store.getState('auth.customer')) setMode('me')
      else store.setState('auth.showLoginModal', true)
    }
  })
}

function renderAuthChip() {
  const chip = document.querySelector('[data-studio-action="auth"]')
  if (!chip) return
  const customer = store.getState('auth.customer')
  if (customer) {
    const name = customer.display_name || customer.name || customer.email?.split('@')[0] || 'Account'
    chip.innerHTML = `<span class="sg-auth-dot" aria-hidden="true"></span>${esc(name)}`
    chip.title = 'Signed in — open your account'
  } else {
    chip.textContent = 'Sign in'
    chip.title = 'Sign in to save your designs'
  }
}

/** Update the current phase (P0-P3) and UI accordingly */
function updatePhase() {
  const hasSource = Boolean(store.getState('sourceImage')?.url)
  const hasPrompt = Boolean(studio.prompt?.trim())
  const canGenerate = hasSource || hasPrompt
  const generateBtn = studio.root?.querySelector('[data-studio-action="generate"]')

  if (!canGenerate) {
    // P0: neither source nor prompt — the key stays CLICKABLE but ghosted; a
    // click triggers the guided empty state (guideSourcePick) instead of
    // sitting dead (Kittl "asks before it creates" / demo v3).
    if (generateBtn) { generateBtn.disabled = false; generateBtn.classList.remove('sg-generate'); generateBtn.classList.add('sg-ghost') }
  } else if (!studio.revealed && !studio.generating) {
    // P1: has source, not revealed, not generating — red CTA is "Generate 4"
    if (generateBtn) { generateBtn.disabled = false; generateBtn.classList.add('sg-generate'); generateBtn.classList.remove('sg-ghost') }
  } else if (studio.generating) {
    // P2: generating — the key shows progress (updateNineProgress owns text)
    if (generateBtn) { generateBtn.disabled = true; generateBtn.classList.remove('sg-generate'); generateBtn.classList.add('sg-ghost') }
  } else if (studio.revealed) {
    // P3: revealed — re-click settles paid cells for free / re-dispatches
    // unfinished ones; the commitment actions live in the tile preview modal.
    if (generateBtn) { generateBtn.disabled = false; generateBtn.classList.add('sg-generate'); generateBtn.classList.remove('sg-ghost') }
  }
  renderPromptPreview()
}

/**
 * Generate (sheet flow, 2026-10-29) = ONE POST /api/customer/create/sheet
 * per click: FLUX-3 renders the 2x2 sheet (prompt-only or source-referenced),
 * the worker slices + white-keys it, and the four dispatched style cells
 * fade in their tiles. The H-129 reveal skeleton is kept — cells stay
 * skeletons until the tiles land.
 *
 * Cost note: one click = one bfl-flux-3-image sheet (~$0.04, 4 tiles). The
 * key re-locks while a batch is in flight; shuffle / pool remounts never
 * auto-fire a paid batch — only this explicit click does.
 */
async function handleGenerate() {
  const hasSource = Boolean(store.getState('sourceImage')?.url)
  const hasPrompt = Boolean(studio.prompt?.trim())
  // P2 (generating) stays a dead key. P0 (neither source nor prompt) is the
  // guided empty state: the ghosted key pulses the source column + bubble.
  if (studio.generating) return
  if (!hasSource && !hasPrompt) {
    guideSourcePick()
    return
  }
  // Iron rule (H-163): a click on an ENABLED generate key must never be
  // swallowed silently — dispatch or tell the user why not.
  if (!studio.materialGrid) {
    showToast('Style grid is not ready yet — try again in a moment', 'error')
    return
  }
  // Guests welcome (2026-11-02 one-click magic): no client-side login gate —
  // the server answers 401 LOGIN_REQUIRED past the free quota, and
  // runSheetGenerate's 401 branch sets _pendingStudioGenerate so sign-in
  // resumes the batch (USER_LOGIN listener).
  // Sheet flow passes the source as a plain https reference url (FLUX-3
  // images array) — the old typed slot-ref contract is gone with the
  // dispatch bridge. An H-167 deep-link url works as-is.
  // Resume path (login gate): do NOT remount on re-entry — a remount is a new
  // paid-batch contract. The batch re-drive below settles already-paid cells
  // in place and re-dispatches only the ones without an image in hand.
  studio.revealed = true
  studio.generating = true
  const hint = studio.root?.querySelector('[data-sg-stage-hint]')
  if (hint) hint.textContent = 'Rendering — watch each style become a design.'
  updatePhase()
  updateNineProgress()
  renderResults()
  try {
    await runSheetGenerate(currentBatch(), studio.materialGrid, studio.genToken)
  } finally {
    studio.generating = false
    studio.sheetDispatch = []
    const btn = studio.root?.querySelector('[data-studio-action="generate"]')
    if (btn) btn.textContent = generateLabel()
    if (hint) {
      const done = currentBatch().filter((c) => c.generated && c.imageUrl).length
      hint.textContent = done
        ? `${done} design${done === 1 ? '' : 's'} ready — click one to preview it on the product.`
        : ''
    }
    updatePhase()
    renderResults()
  }
}

/**
 * One click = ONE sheet request (bao 2026-10-29 2x2@1024 ruling): the
 * backend renders the 2x2 FLUX-3 sheet (prompt-only, or img2img from the
 * source url), slices and white-keys it, and returns 4 tile URLs aligned to
 * the dispatched style ids. Only the first SHEET_BATCH unfinished cells ride
 * a click — the rest stay skeletons for the next click (incremental spend).
 * Cells that already hold a paid result just settle back into the grid
 * (H-163 cost guardrail: batch re-entry never re-buys a finished tile).
 */
async function runSheetGenerate(batch, grid, token) {
  const pending = batch.filter((cell) => !(cell.generated && cell.imageUrl))
  for (const cell of batch) {
    if (cell.generated && cell.imageUrl) await grid.completeCell(cell.id)
  }
  const dispatch = pending.slice(0, SHEET_BATCH)
  if (!dispatch.length) return
  studio.sheetDispatch = dispatch.map((cell) => String(cell.id))
  const srcUrl = store.getState('sourceImage')?.url
  // The typed note + every checked prompt piece (the photo IS the prompt).
  const prompt = composeSheetPrompt()
  let sheet
  try {
    sheet = await generateSheet({
      ...(prompt ? { prompt } : {}),
      ...(srcUrl ? { image_url: srcUrl } : {}),
      style_ids: dispatch.map((cell) => String(cell.id)),
      onStatus: ({ status, elapsedMs }) => {
        if (token !== studio.genToken || studio.act !== 'gallery') return
        const btn = studio.root?.querySelector('[data-studio-action="generate"]')
        if (!btn || !studio.generating) return
        const secs = Math.max(1, Math.round(elapsedMs / 1000))
        btn.textContent =
          status === 'queued' ? `Queued… ${secs}s` : `Rendering… ${secs}s`
      },
    })
  } catch (err) {
    if (token !== studio.genToken || studio.act !== 'gallery') return
    log.warn('sheet generation failed:', err?.message || err)
    // 401 = guest quota spent (or expired token). postJson already opened the
    // login modal — park the cells back as pending and arm the resume flag
    // instead of painting failures over a sign-in prompt.
    if (/\(401\)|HTTP 401|unauthorized/i.test(String(err?.message || err))) {
      _pendingStudioGenerate = true
      for (const cell of dispatch) grid.resetCell(cell.id)
      return
    }
    // Only the dispatched cells failed — the undispatched rest stay pending.
    for (const cell of dispatch) grid.failCell(cell.id, err, describeGenerateError(err))
    toastGenerateError(err)
    return
  }
  if (token !== studio.genToken || studio.act !== 'gallery') return
  // Tiles align to the style_ids we sent — a missing tile fails its own
  // cell (never borrow a positional slot: that would hang the wrong style's
  // art on a cell and corrupt the intent grid's alignment).
  for (const cell of dispatch) {
    const tile = sheet.tiles.find((t) => String(t.style_id) === String(cell.id))
    if (!tile?.image_url) {
      grid.failCell(cell.id, new Error('missing tile'), 'Generation failed — tap to retry')
      continue
    }
    cell.imageUrl = tile.image_url
    cell.assetId = null
    cell.generated = true
    await grid.completeCell(cell.id)
  }
}

/** User-visible copy per failure class (quota / auth / upstream 4xx / other). */
function describeGenerateError(err) {
  const msg = String(err?.message || err || '')
  if (msg.match(/\(401\)|\(403\)|unauthorized|forbidden/i)) {
    return 'Sign in to keep creating'
  }
  if (/\(429\)|too many|budget|配额/i.test(msg)) {
    return 'Out of quota — try again later'
  }
  if (/\(4\d\d\)/.test(msg)) return 'Generation request failed — try again'
  return 'Generation failed — tap to retry'
}

/** One toast per batch — a shared failure (quota/429) fails all 9 cells at once. */
function toastGenerateError(err) {
  if (_lastErrorToastToken === studio.genToken) return
  _lastErrorToastToken = studio.genToken
  showToast(describeGenerateError(err), 'error')
}

/**
 * Single-cell retry (failed nine-cell tap).
 */
async function retryGenerateCell(id) {
  const item = currentBatch().find((c) => String(c.id) === String(id))
  if (!item || item.generated) return

  // Cost guardrail (H-163): a rapid double-tap on [Retry] must bill the cell
  // exactly once — the first tap detaches the button (skeleton swap) but its
  // listener can still fire once more; the in-flight flag gates that. The
  // GLOBAL generating flag gates the cross-cell case: two failed cells tapped
  // in parallel would both see the same pending set and bill two sheets.
  if (item.retryInFlight || studio.generating) return

  if (studio.materialGrid) studio.materialGrid.resetCell(id)
  item.retryInFlight = true
  studio.generating = true
  updatePhase()
  try {
    // Sheet flow: retry re-runs the sheet for the unfinished cells only
    // (finished cells settle without re-billing — H-163).
    await runSheetGenerate(currentBatch(), studio.materialGrid, studio.genToken)
  } finally {
    item.retryInFlight = false
    studio.generating = false
    studio.sheetDispatch = []
    const btn = studio.root?.querySelector('[data-studio-action="generate"]')
    if (btn) btn.textContent = generateLabel()
    updatePhase()
  }
}

/**
 * P3 single-cell refine (preview modal): re-render JUST the previewed style
 * as a full 1024² canvas with the (possibly edited) refine prompt. Cost is
 * honest — one deliberate $0.048 render, never bundled into a batch.
 * Failure keeps the old tile: the cell restores its image and a toast
 * carries the reason (a failure face would hide the still-valid tile).
 */
async function refinePreviewCell() {
  const item = studio.previewModalItem
  if (!item || !studio.materialGrid) return
  const cell = currentBatch().find((c) => String(c.id) === String(item.id))
  if (!cell || !(cell.generated && cell.imageUrl)) return
  // Cost guardrail: one in-flight refine per cell, and never mid-batch —
  // a batch dispatch would see this cell as pending and re-bill it.
  if (cell.refining || studio.generating) {
    if (studio.generating) showToast('Finish the current batch first', 'error')
    return
  }
  // Guests refine too — the server enforces the free quota (401 → modal).
  const prompt = studio.root?.querySelector('[data-sg-refine-prompt]')?.value?.trim() ?? ''
  const srcUrl = store.getState('sourceImage')?.url
  const token = studio.genToken
  const priorImageUrl = cell.imageUrl
  cell.refining = true
  cell.generated = false
  cell.imageUrl = undefined
  cell.failure = null
  closePreviewModal()
  renderResults()
  try {
    const result = await generateCell({
      style_id: String(cell.id),
      ...(prompt ? { prompt } : {}),
      ...(srcUrl ? { image_url: srcUrl } : {}),
    })
    if (token !== studio.genToken || studio.act !== 'gallery') return
    if (!result.tile?.image_url) throw new Error('missing tile')
    cell.imageUrl = result.tile.image_url
    cell.generated = true
    cell.assetId = null
    await studio.materialGrid.completeCell(cell.id)
  } catch (err) {
    if (token !== studio.genToken || studio.act !== 'gallery') return
    log.warn('cell refine failed:', err?.message || err)
    cell.imageUrl = priorImageUrl
    cell.generated = true
    renderResults()
    showToast(describeGenerateError(err), 'error')
  } finally {
    cell.refining = false
  }
}

// ---- inline library (attach → waterfall, no drawer) ------------------------

async function toggleLibrary() {
  const panel = studio.root?.querySelector('[data-sg-library]')
  if (!panel) return
  if (panel.hidden) {
    studio.libraryPickId = null
    panel.hidden = false
    await renderLibrary()
  } else {
    closeLibrary()
  }
}

async function renderLibrary() {
  const grid = studio.root?.querySelector('[data-sg-library-grid]')
  if (!grid) return
  grid.innerHTML = `<div class="sg-lib-upload" data-sg-lib-upload><span class="plus">＋</span> Upload new</div>`
  const images = store.getState('gallery.images') || []
  if (!images.length) {
    // Shared loading standard: shimmer blocks, not a text line.
    grid.insertAdjacentHTML('beforeend', '<div class="sg-skel sg-lib-skel"></div>'.repeat(8))
    try {
      await loadGallery()
    } catch {
      /* the grid below renders whatever the store holds */
    }
  }
  grid.querySelectorAll('.sg-lib-skel').forEach((el) => el.remove())
  const list = store.getState('gallery.images') || []
  if (!list.length) {
    grid.insertAdjacentHTML('beforeend', '<div class="sg-lib-loading">No images yet — upload your first</div>')
    return
  }
  // Quick-pick thumbs (2026-10-27, bao): the library is a picker, not a
  // browsing surface — plain thumbs; the Design jump appears on hover.
  grid.insertAdjacentHTML(
    'beforeend',
    list
      .map(
        (img) => `<button type="button" class="sg-lib-thumb" data-sg-lib-pick="${esc(img.id)}">
          <img src="${esc(img.thumbnail || img.url)}" alt="${esc(img.title || '')}" loading="lazy">
          <span class="sg-lib-design" data-studio-action="lib-design" data-sg-lib-design="${esc(img.id)}">→ Design</span>
        </button>`
      )
      .join('')
  )
  syncLibraryFoot()
}

/** Footer state mirrors the selected thumb (dashed placeholder until then). */
function syncLibraryFoot() {
  const hint = studio.root?.querySelector('[data-sg-library-hint]')
  const go = studio.root?.querySelector('[data-sg-library-go]')
  const images = store.getState('gallery.images') || []
  const pick = images.find((i) => String(i.id) === String(studio.libraryPickId))
  if (hint) {
    hint.textContent = pick ? pick.title || `Material #${pick.id}` : 'Pick a material'
    hint.classList.toggle('sg-empty', !pick)
  }
  if (go) go.disabled = !pick
}

function closeLibrary() {
  const panel = studio.root?.querySelector('[data-sg-library]')
  if (panel) panel.hidden = true
  studio.libraryPickId = null
}

function pickLibraryImage(el) {
  const images = store.getState('gallery.images') || []
  const img = images.find((i) => String(i.id) === el.dataset.sgLibPick)
  if (!img) return
  // Select-only: the card highlights and the fixed footer's Generate commits
  // (2026-10-27, bao — the modal no longer vanishes on the first click).
  studio.libraryPickId = img.id
  studio.root?.querySelectorAll('.sg-lib-thumb.sel').forEach((c) => c.classList.remove('sel'))
  el.classList.add('sel')
  syncLibraryFoot()
}

/** Footer Generate: commit the selected card as the source and dispatch. */
function handleLibraryGenerate() {
  const images = store.getState('gallery.images') || []
  const img = images.find((i) => String(i.id) === String(studio.libraryPickId))
  if (!img) return
  closeLibrary()
  // image-roles contract: one source, the new pick replaces the old; its
  // IMAGE_SELECT emission re-enters the gallery act with it (vision read
  // repopulates the pieces row via VISION_COMPLETE).
  useImageAsSource({ url: img.url, id: img.id, title: img.title }, 'library')
  handleGenerate()
}

function renderAct() {
  if (!studio.root) return
  const stage = studio.root.querySelector('[data-studio-stage]')
  if (!stage) return
  closeSources()
  stage.innerHTML = galleryHtml()
  studio.root.dataset.act = studio.act
  const back = studio.root.querySelector('[data-studio-action="back"]')
  if (back) back.hidden = studio.act !== 'gallery'
  toggleStudioFocus(true)
  if (studio.act === 'gallery') {
    renderSourceBand()
    renderIntentStrip()
    renderIntentPicker()
    renderResults()
    renderPieces()
    const promptEl = stage.querySelector('[data-sg-design-prompt]')
    if (promptEl) promptEl.value = studio.prompt
    renderAuthChip()
    renderPromptPreview()
  } else {
    toggleRpOverlay(false)
    closePreviewModal()
  }
}

function toggleStudioFocus(on) {
  const appEl = document.querySelector('.app')
  const rightPanel = document.querySelector('.right-panel')
  if (appEl) {
    appEl.classList.toggle('studio-focus', on)
  }
  if (!on && rightPanel) {
    rightPanel.classList.remove('rp-overlay')
    studio.rpOverlayOpen = false
  }
}

function toggleRpOverlay(open) {
  const rightPanel = document.querySelector('.right-panel')
  if (!rightPanel) return
  
  studio.rpOverlayOpen = open
  rightPanel.classList.toggle('rp-overlay', open)
  
  if (open) {
    // Add click outside listener
    document.addEventListener('click', handleRpOverlayClickOutside, true)
  } else {
    document.removeEventListener('click', handleRpOverlayClickOutside, true)
  }
}

function handleRpOverlayClickOutside(e) {
  const rightPanel = document.querySelector('.right-panel')
  if (rightPanel && !rightPanel.contains(e.target) && 
      !e.target.closest('[data-rp-reopen]')) {
    toggleRpOverlay(false)
  }
}

// ---- Act 2 · gallery + cell-by-cell reveal ---------------------------------

function currentBatch() {
  const pool = studio.stylePool
  if (!pool.length) return []
  const offset = studio.batchOffset % pool.length
  const rotated = pool.slice(offset).concat(pool.slice(0, offset))
  return rotated.slice(0, FILM_SIZE)
}

function startGallery() {
  if (!studio.root) return
  studio.act = 'gallery'
  studio.batchOffset = 0
  studio.revealed = false
  studio.lockedIntentCellIds = new Set()
  renderAct()
  // Initialize with seed items
  studio.stylePool = wallItems([], STYLE_POOL_MIN)
  mountGrids(currentBatch())
  updatePhase()
  loadStylePool()
    .then((pool) => {
      if (studio.act !== 'gallery' || !studio.materialGrid) return
      // Shopify 灵感墙 deep link (?style=<id>): lead the batch with the
      // linked style so the visitor sees exactly what they tapped. One-shot
      // per gallery entry; ignored when the id isn't in the pool.
      if (!studio.deepLinkConsumed) {
        const wanted = new URLSearchParams(window.location.search).get('style')
        const idx = wanted ? pool.findIndex((s) => String(s.id) === String(wanted)) : -1
        if (idx > 0) {
          pool.unshift(pool.splice(idx, 1)[0])
          studio.batchOffset = 0
        }
        studio.deepLinkConsumed = true
      }
      // P0 (H-163): never tear down an in-flight paid batch. The remount below
      // bumps genToken, which aborts every not-yet-dispatched cell — witnessed
      // live as "clicked generate, zero API dispatch" when the styles fetch
      // resolved just after the click. The fresh pool applies on the next
      // shuffle / gallery entry instead.
      if (studio.generating) return
      const currentIds = currentBatch().map(i => i.id).join(',')
      const newIds = pool.slice(0, FILM_SIZE).map(i => i.id).join(',')
      if (pool.length && currentIds !== newIds) {
        studio.stylePool = pool
        mountGrids(currentBatch())
        warmIntentMockups()
        if (studio.revealed) {
          studio.revealed = false
          updatePhase()
        }
      }
    })
    .catch((err) => {
      log.warn('styles fetch failed, keeping the seed batch:', err?.message || err)
    })
}

async function loadStylePool() {
  const res = await apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles`)
  return wallItems(res?.data?.styles ?? [], STYLE_POOL_MIN)
}

/**
 * Rotation telemetry (H-127 epsilon-greedy): a tile pick = one selection for
 * that style. Fire-and-forget — telemetry must never block the preview.
 */
function reportStyleSelect(styleId) {
  if (styleId == null) return
  apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style_id: String(styleId) }),
  }).catch((err) => log.warn('select telemetry failed:', err.message))
}

/**
 * P1 purchase telemetry: a buy-click = one purchase for that style (the
 * create funnel's closest buy signal; the purchases column was schema-only
 * until 2026-10-25). Fire-and-forget — telemetry must never block checkout.
 */
function reportStylePurchase(styleId) {
  if (styleId == null) return
  apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles/purchase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style_id: String(styleId) }),
  }).catch((err) => log.warn('purchase telemetry failed:', err.message))
}

/** Open preview modal showing material and mockup for an item */
function openPreviewModal(item) {
  if (!studio.root) return
  studio.previewModalOpen = true
  studio.previewModalItem = item
  const modal = studio.root.querySelector('[data-sg-preview-modal]')
  if (modal) modal.hidden = false
  const refineInput = studio.root.querySelector('[data-sg-refine-prompt]')
  if (refineInput) refineInput.value = studio.prompt || ''
  // Reset any prior buy outcome (a previous open may have swapped the action
  // row to the PDP link).
  buyStatus('')
  const actions = studio.root.querySelector('.sg-preview-modal-actions')
  if (actions && !actions.querySelector('[data-sg-buy]')) {
    actions.innerHTML = `
      <button class="sg-ghost sg-use-material" type="button" data-studio-action="use-material">↺ Use as material</button>
      <button class="sg-ghost sg-buy" type="button" data-studio-action="buy" data-sg-buy>Buy</button>
      <button class="sg-use-design" type="button" data-studio-action="use-design">Use this design →</button>`
  }
  renderPreviewBlanks()
  updatePreviewModal(item)
}

/** Carrier chips (Batch B product preview): the blank catalog from
 *  GET /api/blanks via loadBlanks(); the active chip mirrors getBlankKey(). */
async function renderPreviewBlanks() {
  const host = studio.root?.querySelector('[data-sg-preview-blanks]')
  if (!host) return
  const blanks = await loadBlanks()
  if (!studio.previewModalOpen) return // closed while the catalog loaded
  const current = getBlankKey()
  host.innerHTML = blanks
    .map(
      (b) =>
        `<button type="button" class="sg-preview-blank${b.key === current ? ' on' : ''}" data-sg-blank="${esc(b.key)}">${esc(b.label)}</button>`
    )
    .join('')
  updateBuyButton()
}

/** Close preview modal */
function closePreviewModal() {
  if (!studio.root) return
  studio.previewModalOpen = false
  studio.previewModalItem = null
  const modal = studio.root.querySelector('[data-sg-preview-modal]')
  if (modal) modal.hidden = true
}

/** Update preview modal images */
async function updatePreviewModal(item) {
  if (!studio.root || !item) return
  const materialImg = studio.root.querySelector('[data-sg-preview-material]')
  const mockupImg = studio.root.querySelector('[data-sg-preview-mockup]')
  if (materialImg && item.imageUrl) {
    materialImg.src = item.imageUrl
    materialImg.hidden = false
  }
  if (mockupImg && item.imageUrl) {
    try {
      const result = await composeMockup(item.imageUrl, getBlankKey())
      // Race guard: a slower compose must not overwrite a newer preview.
      if (studio.previewModalItem !== item) return
      mockupImg.src = result.dataUrl
      mockupImg.hidden = false
    } catch {
      mockupImg.hidden = true
    }
  }
}

function nextBatch() {
  if (!studio.stylePool.length) return
  // Keep locked cells, shuffle the rest
  const currentItems = currentBatch()
  const lockedItems = currentItems.filter(i => studio.lockedIntentCellIds.has(String(i.id)))
  const lockedIds = new Set(lockedItems.map(i => String(i.id)))
  
  // Collect available styles (not locked and not already in lockedItems)
  const availableStyles = studio.stylePool.filter(s => !lockedIds.has(String(s.id)))
  const numToReplace = FILM_SIZE - lockedItems.length
  
  if (numToReplace > 0 && availableStyles.length > 0) {
    // Shuffle available styles
    for (let i = availableStyles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [availableStyles[i], availableStyles[j]] = [availableStyles[j], availableStyles[i]]
    }
    // Create new batch: locked items + new items, preserving order (fill in non-locked positions)
    const newBatch = []
    const usedIds = new Set(lockedItems.map((i) => String(i.id)))
    let newItemIdx = 0
    for (const item of currentItems) {
      if (studio.lockedIntentCellIds.has(String(item.id))) {
        newBatch.push(item)
      } else {
        // Skip candidates already placed — a duplicate style id in one batch
        // would hang the same art on two cells (small-pool edge).
        let candidate = null
        while (newItemIdx < availableStyles.length && !candidate) {
          const c = availableStyles[newItemIdx++]
          if (!usedIds.has(String(c.id))) candidate = c
        }
        if (candidate) {
          usedIds.add(String(candidate.id))
          newBatch.push(candidate)
        } else {
          // Pool exhausted (pool < FILM_SIZE): keep the old item rather than
          // duplicating a style already on the board.
          newBatch.push(item)
        }
      }
    }
    // Now, let's adjust the style pool or just reorder it so currentBatch() returns this new batch
    const newPool = [...newBatch]
    for (const style of studio.stylePool) {
      if (!newBatch.some(b => b.id === style.id)) {
        newPool.push(style)
      }
    }
    studio.stylePool = newPool
    studio.batchOffset = 0
  } else {
    // If all are locked or no available styles, just increment offset to cycle
    studio.batchOffset += FILM_SIZE
  }
  
  mountGrids(currentBatch())
  // H-150: a shuffled batch is 9 NEW paid BFL calls — never auto-fire them.
  if (studio.revealed) {
    studio.revealed = false
    updatePhase()
  }
}

/** The preview modal's commitment actions act on the tile being previewed. */
function previewItem() {
  return studio.previewModalItem || currentBatch()[0]
}

/**
 * H-140 Create exit — the picked cell leaves for Design as a MATERIAL
 * (?mode=design&design_material={id}&ref=create contract): post-pick upscale
 * persists the chosen material first (pipeline: pick -> upscale -> designer),
 * then Design opens with it located + preselected at the Arts head
 * (design.entryMaterial -> selectArtsMaterial; locate-only, never auto-injects
 * the canvas). Applied in-page — the SPA router has no URL routing — with the
 * URL reflected for deep-link parity. When the upscale tool is unreachable
 * (dev / placeholder seeds) the raw cell image rides along so the exit never
 * dead-ends.
 */
/**
 * Design handoff shared by use-design (a generated tile) and lib-design (a
 * library material): entryMaterial state + URL reflection + mode switch.
 * design_material is an images-table integer id — reflected only when the
 * material actually carries one (a bare url stays in-page state).
 */
function navigateToDesign(material) {
  writeRefAttribution('create')
  store.setState('design.entryMaterial', material)
  try {
    const pageUrl = new URL(window.location.href)
    pageUrl.searchParams.set('mode', 'design')
    if (material.id != null && /^[0-9]+$/.test(String(material.id))) {
      pageUrl.searchParams.set('design_material', String(material.id))
    }
    pageUrl.searchParams.set('ref', 'create')
    window.history.replaceState(null, '', pageUrl.href)
  } catch {
    /* non-DOM context — the URL reflection is best-effort */
  }
  setMode('design')
}

/** Library card → Design (2026-10-27, bao): every material jumps straight
 *  to the design page. Library images are already cataloged assets — no
 *  upscale needed. */
function handleLibDesign(el) {
  const images = store.getState('gallery.images') || []
  const img = images.find((i) => String(i.id) === String(el.dataset.sgLibDesign))
  if (!img?.url) return
  navigateToDesign({ id: img.id, url: img.url, title: img.title })
}

async function handleUseDesign(button) {
  if (button.disabled) return
  const item = previewItem()
  if (!item?.imageUrl) return
  button.disabled = true
  try {
    const res = await upscaleImage(item.imageUrl, 4).catch((err) => {
      log.warn('use-design upscale failed, handing off the raw material:', err?.message || err)
      return null
    })
    const material = {
      id: res?.asset_id ?? null,
      url: res?.url || item.imageUrl,
      title: item.label,
    }
    navigateToDesign(material)
  } finally {
    button.disabled = false
  }
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/** Buy button label mirrors the active carrier's governed price; preview-only
 *  carriers (buyable:false) disable the button instead of lying. */
async function updateBuyButton() {
  const btn = studio.root?.querySelector('[data-sg-buy]')
  if (!btn) return
  const buyable = await getActiveBuyable().catch(() => null)
  if (!studio.previewModalOpen && !btn.isConnected) return
  if (buyable) {
    btn.disabled = false
    btn.textContent = `Buy · ${USD.format(buyable.priceUsdCents / 100)}`
    btn.title = buyable.label
  } else {
    btn.disabled = true
    btn.textContent = 'Buy'
    btn.title = 'This carrier is preview-only for now'
  }
}

function buyStatus(text) {
  const el = studio.root?.querySelector('[data-sg-buy-status]')
  if (!el) return
  el.hidden = !text
  el.textContent = text || ''
}

/**
 * One-click buy (2026-11-02): the tile goes straight through the server lane
 * (SDS produce → customer-owned design version → Shopify publish) and the
 * modal ends on a real PDP link. Replaces the old "Add to cart" label-lie
 * that only mode-switched into the designer with a flattened mockup.
 */
async function handleBuy(button) {
  if (button.disabled) return
  const selectedItem = previewItem()
  if (!selectedItem?.imageUrl) return
  // Auth gate first (2026-10-31 review): the buy endpoints are
  // customer-only; an anonymous click used to 401 the telemetry silently.
  if (!requireAuth()) return
  button.disabled = true
  try {
    const buyable = await getActiveBuyable()
    if (!buyable) {
      showToast('This product is not available for direct purchase yet', 'error')
      return
    }
    // P1 purchase telemetry (authed now): buy-click feeds the style-scoring loop.
    reportStylePurchase(selectedItem.id)

    buyStatus('Creating your product — this takes a few minutes…')
    const { buy_id } = await startBuy({
      image_url: selectedItem.imageUrl,
      product_id: buyable.productId,
    })
    const state = await waitForBuy(buy_id, {
      onUpdate: ({ status }) => {
        buyStatus(
          status === 'publishing'
            ? 'Almost there — listing your product…'
            : 'Creating your product — this takes a few minutes…'
        )
      },
    })
    if (!state.product_url) throw new Error('Product link missing')
    buyStatus('')
    // Swap the action row to the real checkout link (new tab — the studio
    // session stays put so the user can keep iterating).
    const actions = studio.root?.querySelector('.sg-preview-modal-actions')
    if (actions) {
      actions.innerHTML = `<a class="sg-buy-now" href="${esc(state.product_url)}" target="_blank" rel="noopener">Buy now · ${USD.format(buyable.priceUsdCents / 100)} →</a>`
    }
    showToast('Your product is live', 'success', 4000)
  } catch (err) {
    log.warn('buy failed:', err?.message || err)
    buyStatus('')
    showToast(err?.message || 'Could not create the product', 'error')
  } finally {
    button.disabled = false
  }
}

/**
 * C→A loop-back (2026-10-25, bao): the previewed 成品图 becomes the new
 * material — materialized into an asset first (the tile is an R2 URL with
 * no asset id), so the source slot keeps its typed 'asset' ref. The
 * sourceImage subscription retires the old C grid: the equation restarts.
 */
async function handleUseAsMaterial(button) {
  if (button.disabled) return
  const item = previewItem()
  if (!item?.imageUrl) return
  button.disabled = true
  try {
    await materializeExternalSource(item.imageUrl)
    closePreviewModal()
    showToast('Set as material — generate again', 'success', 2000)
  } catch (err) {
    log.warn('use-as-material failed:', err?.message || err)
    showToast('Could not use as material', 'error')
  } finally {
    button.disabled = false
  }
}

// ---- Four-pipe source overlay ----------------------------------------------

function toggleSources() {
  const panel = studio.root?.querySelector('[data-studio-sources]')
  if (!panel) return
  if (panel.hidden) openSources()
  else closeSources()
}

function openSources() {
  const panel = studio.root?.querySelector('[data-studio-sources]')
  if (!panel) return
  panel.innerHTML = `
    <button type="button" data-source-pipe="upload"><span class="sg-ic"><i class="ph ph-upload-simple" aria-hidden="true"></i></span>Upload</button>
    <button type="button" data-source-pipe="library"><span class="sg-ic"><i class="ph ph-images" aria-hidden="true"></i></span>Library</button>
    <button type="button" data-source-pipe="describe"><span class="sg-ic"><i class="ph ph-pencil-line" aria-hidden="true"></i></span>Describe</button>`
  panel.hidden = false
  panel.querySelectorAll('[data-source-pipe]').forEach((button) =>
    button.addEventListener('click', () => {
      const pipe = button.dataset.sourcePipe
      if (pipe === 'upload') pickUpload()
      if (pipe === 'library') {
        closeSources()
        toggleLibrary()
      }
      if (pipe === 'describe') showDescribeForm(panel)
    })
  )
}

function closeSources() {
  const panel = studio.root?.querySelector('[data-studio-sources]')
  if (panel) panel.hidden = true
}

// H-131: ?entry=sources — the storefront "use your own image" funnel lands on
// the hero act with the four-pipe source picker already open (upload pipe is
// the first button). The source param funnel (?source={images.id}) needs no
// helper: useImageAsSource's IMAGE_SELECT emission drives startGallery via
// the listener in bindGlobalListeners.
function openEntrySources() {
  if (!studio.root) return
  // Don't switch to hero act anymore; just open sources panel directly
  openSources()
}

function pickUpload() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.addEventListener('change', async () => {
    const file = input.files?.[0]
    if (!file) return
    try {
      const fd = new FormData()
      fd.append('image', file)
      fd.append('source', 'source')
      const asset = await uploadImage(fd)
      const url = asset?.public_url || asset?.url
      if (!url || asset?.id == null) throw new Error('upload returned no asset')
      // Emits IMAGE_SELECT → the gallery act opens with this source.
      useImageAsSource({ url, id: asset.id, name: file.name }, 'asset')
      closeSources()
    } catch (err) {
      log.error('source upload failed:', err)
      showToast('Upload failed — try again', 'error')
    }
  })
  input.click()
}

function showDescribeForm(panel) {
  panel.innerHTML = `
    <div class="sg-describe">
      <textarea data-sg-prompt rows="3" placeholder="Describe the artwork in one line"></textarea>
      <button type="button" class="sg-buy" data-sg-describe-go>Generate ideas</button>
    </div>`
  panel.querySelector('[data-sg-describe-go]')?.addEventListener('click', () => {
    const text = panel.querySelector('[data-sg-prompt]')?.value?.trim()
    if (!text) {
      showToast('Describe the artwork first', 'error')
      return
    }
    // Reuse the existing 4-ideas pipeline (task card section 7): the L2 workbench owns dispatch.
    store.setState('creation.textInput', text)
    store.setState('creation.intentCard', L0_DEFAULT_INTENT)
    closeSources()
    setTier(TIERS.L2)
    showLegacyShell()
  })
}

// ---- legacy shell handoff (L2 workbench, pipeline untouched) --------------

function showLegacyShell() {
  if (!studio.root) return
  studio.root.hidden = true
  let legacy = document.getElementById('legacy-create-shell')
  if (!legacy) {
    legacy = document.createElement('div')
    legacy.id = 'legacy-create-shell'
    const modeCreate = document.getElementById('mode-create')
    if (modeCreate) modeCreate.appendChild(legacy)
  }
  legacy.hidden = false
  // Remove studio-focus when showing legacy shell
  toggleStudioFocus(false)
}

function showStudioShell() {
  if (!studio.root) return
  studio.root.hidden = false
  const legacy = document.getElementById('legacy-create-shell')
  if (legacy) legacy.hidden = true
  // Apply studio-focus when showing studio shell
  toggleStudioFocus(true)
  renderAct()
}

// ---- lifecycle ------------------------------------------------------------

function bindShell() {
  // Delegated input handler — the bar's prompt field.
  studio.root.querySelector('[data-studio-stage]')?.addEventListener('input', (event) => {
    if (event.target.closest('[data-sg-design-prompt]')) {
      studio.prompt = event.target.value
      updatePhase()
    }
  })
  // Enter in the bar generates (demo v4).
  studio.root.querySelector('[data-studio-stage]')?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.closest('[data-sg-design-prompt]')) {
      handleGenerate()
    }
  })
  studio.root.querySelector('[data-studio-stage]')?.addEventListener('click', (event) => {
    // Prompt piece toggle (the photo IS the prompt — checked pieces join the sheet prompt)
    const pieceEl = event.target.closest('[data-piece-index]')
    if (pieceEl) {
      togglePiece(Number(pieceEl.dataset.pieceIndex))
      return
    }
    // Toggle preview modal close
    const closeEl = event.target.closest('[data-sg-preview-close]')
    if (closeEl) {
      closePreviewModal()
      return
    }
    // Preview modal carrier chip — switch blank, recompose the mockup
    // client-side (free; the material tile is unchanged).
    const blankEl = event.target.closest('[data-sg-blank]')
    if (blankEl) {
      setBlankKey(blankEl.dataset.sgBlank)
      renderPreviewBlanks()
      if (studio.previewModalItem) updatePreviewModal(studio.previewModalItem)
      // 成品图 follows the carrier: re-compose every intent thumb's after.
      renderIntentStrip()
      renderIntentPicker()
      return
    }
    // Intent strip: thumb tap opens its swap modal…
    const slotEl = event.target.closest('[data-sg-intent-slot]')
    if (slotEl) {
      if (!studio.generating) toggleIntentPicker(Number(slotEl.dataset.sgIntentSlot))
      return
    }
    // …backdrop / × closes it without swapping…
    if (event.target.closest('[data-sg-intent-close]')) {
      studio.intentPickerSlot = null
      renderIntentPicker()
      return
    }
    // …candidate tap swaps it into the open slot (the image IS the intent).
    const pickEl = event.target.closest('[data-sg-intent-pick]')
    if (pickEl) {
      if (!studio.generating && studio.intentPickerSlot != null) {
        swapIntent(studio.intentPickerSlot, pickEl.dataset.sgIntentPick)
      }
      return
    }
    // Results grid: failed cell tap retries just that cell…
    const retryEl = event.target.closest('[data-nine-retry]')
    if (retryEl) {
      retryGenerateCell(retryEl.dataset.nineRetry)
      return
    }
    // …OUT tile click opens the product preview modal.
    const viewEl = event.target.closest('[data-nine-view]')
    if (viewEl) {
      const item = currentBatch().find((c) => String(c.id) === String(viewEl.dataset.nineView))
      if (item?.imageUrl) {
        // One selection per tile per batch (2026-10-25 review): reopening
        // the preview N times must not record N selections per impression.
        if (!item.selectReported) {
          item.selectReported = true
          reportStyleSelect(item.id)
        }
        openPreviewModal(item)
      }
      return
    }
    // Inline library cells
    const uploadEl = event.target.closest('[data-sg-lib-upload]')
    if (uploadEl) {
      pickUpload()
      return
    }
    const libEl = event.target.closest('[data-sg-lib-pick]')
    // Inner actions (the card's → Design button) win over the card select.
    if (libEl && !event.target.closest('[data-studio-action]')) {
      pickLibraryImage(libEl)
      return
    }
    // Inspiration panel reopen (lives in the gallery topbar)
    if (event.target.closest('[data-rp-reopen]')) {
      toggleRpOverlay(!studio.rpOverlayOpen)
      return
    }
    // Studio actions
    const el = event.target.closest('[data-studio-action]')
    if (!el) return
    const action = el.dataset.studioAction
    if (action === 'back') setMode('gallery')
    if (action === 'start') startGallery()
    if (action === 'source') toggleSources()
    if (action === 'buy') handleBuy(el)
    if (action === 'use-material') handleUseAsMaterial(el)
    if (action === 'use-design') handleUseDesign(el)
    if (action === 'refine-cell') refinePreviewCell()
    if (action === 'toggle-library') toggleLibrary()
    if (action === 'close-library') closeLibrary()
    if (action === 'library-generate') handleLibraryGenerate()
    if (action === 'lib-design') handleLibDesign(el)
    // A tile tap: with a material set it opens the library (swap); empty it
    // points at the input bar's attach slot instead of opening a drawer the
    // user didn't ask for (2026-10-25, bao).
    if (action === 'material-tap') {
      if (store.getState('sourceImage')?.url) toggleLibrary()
      else guideSourcePick()
    }
    if (action === 'clear-source') clearSource()
    if (action === 'shuffle') nextBatch()
    if (action === 'generate') handleGenerate()
    if (action === 'auth') {
      if (store.getState('auth.customer')) setMode('me')
      else store.setState('auth.showLoginModal', true)
    }
  })
  // Escape key to close preview modal
  studio.previewModalEscListener = (event) => {
    if (event.key === 'Escape' && studio.previewModalOpen) {
      closePreviewModal()
      event.stopImmediatePropagation()
    }
  }
  window.addEventListener('keydown', studio.previewModalEscListener, true)
}

function bindGlobalListeners() {
  if (studio.listenersBound) return
  studio.listenersBound = true
  // Every registration returns an unsubscribe — collected here and run by
  // deactivate(). Without this, each deactivate→activate cycle re-registered
  // every handler (listener leak: duplicate startGallery / pieces writes).
  studio._unsubs = studio._unsubs || []
  studio._unsubs.push(eventBus.on(EVENTS.IMAGE_SELECT, (payload) => {
    if (payload?.role === 'reference') return
    if (store.getState('mode') !== 'create') return
    // A fresh source drops the previous photo's pieces; the new vision read
    // repopulates them via VISION_COMPLETE (noVision picks skip the read).
    studio.pieces = []
    studio.piecesLoading = !payload?.noVision
    showStudioShell()
    // Mid-generation remount guard (2026-10-25 review): startGallery →
    // mountGrids bumps genToken; if a paid batch is in flight its tiles are
    // silently discarded on landing. Let the batch land first — the grid
    // reset then happens via the sourceImage subscription.
    if (!studio.generating) startGallery()
    renderPieces()
  }))
  // A generate blocked by the login gate resumes on its own after sign-in
  // (same _pendingGenerate pattern as create/index.js — no second click).
  studio._unsubs.push(eventBus.on(EVENTS.USER_LOGIN, () => {
    if (!_pendingStudioGenerate) return
    _pendingStudioGenerate = false
    if (store.getState('mode') !== 'create' || studio.act !== 'gallery') return
    handleGenerate()
  }))
  // External source writes refresh source band + generate gate.
  studio._unsubs.push(store.subscribe('auth.customer', () => renderAuthChip()))
  // Belt-and-suspenders: saveAuth also emits AUTH_CHANGED, and the chip may
  // be (re)created after the store write — catch both signals.
  studio._unsubs.push(eventBus.on(EVENTS.AUTH_CHANGED, () => renderAuthChip()))
  studio._unsubs.push(store.subscribe('sourceImage', () => {
    if (studio.act !== 'gallery' || studio.root?.hidden) return
    if (!store.getState('sourceImage')?.url) {
      studio.pieces = []
      studio.piecesLoading = false
    }
    // New equation: a changed A retires the live C grid — those tiles were
    // made from the old material and no longer belong to the recipe row
    // (2026-10-25, bao). Skipped mid-generation: a batch in flight still
    // belongs to the A it was dispatched with.
    const batch = currentBatch()
    if (!studio.generating && batch.some((c) => c.generated || c.failure)) {
      resetBatchCells(batch)
      updateNineProgress()
    }
    renderSourceBand()
    renderPieces()
    renderResults()
    // One-click magic (bao 2026-11-02): a freshly landed source IS the click
    // — auto-fire the first paid batch. Deferred to the next tick so a
    // synchronous follow-up (test click, programmatic seed) wins the race:
    // the guards re-check at fire time and no-op once a batch is in flight
    // or cells already hold paid results (H-163).
    const landed = Boolean(store.getState('sourceImage')?.url)
    if (landed) {
      setTimeout(() => {
        const stillLanded = Boolean(store.getState('sourceImage')?.url)
        const fresh = !currentBatch().some((c) => c.generated || c.failure)
        if (stillLanded && fresh && !studio.generating && studio.act === 'gallery') {
          handleGenerate()
        }
      }, 0)
    }
  }))
  // The vision read of the source lands as toggleable prompt pieces in the
  // left column (the photo IS the prompt — demo v3). DEFAULT_ON categories
  // start checked, same rule as the legacy inspiration workspace.
  studio._unsubs.push(eventBus.on(EVENTS.VISION_COMPLETE, (result) => {
    const cards = result?.cards || result?.data?.cards || []
    studio.pieces = cards.map((c, i) => {
      const category = String(c?.category || c?.card_type || 'Style')
      return {
        id: String(c?.id ?? c?.card_id ?? `v${i}`),
        title: c?.title || c?.name || category,
        description: c?.description || c?.prompt_text || '',
        category,
        on: DEFAULT_ON.has(category.toLowerCase()),
      }
    })
    studio.piecesLoading = false
    renderPieces()
    renderPromptPreview()
  }))
  // H-129: Esc must only return to hero — main.js's global requestClose must not close the
  // whole modal-shell (that would regress to a blank page). main.js listens at the document
  // bubble phase; the window capture phase fires first, so consuming the event here blocks it.
  studio.escKeydownListener = (event) => {
    if (event.key !== 'Escape') return
    if (studio.root?.hidden) return
    // An open workbench drawer owns Esc first (its own capture-phase handler
    // closes it) — the gallery fallback fires on the NEXT Esc.
    event.stopImmediatePropagation()
    if (!closeSourcesIfOpen()) setMode('gallery')
  }
  window.addEventListener('keydown', studio.escKeydownListener, true)
}

/** Show studio (for tier switch from L2 back to L1). */
export function showStudio() {
  showStudioShell()
}

/** Check and close sources overlay if open; returns true if closed. */
function closeSourcesIfOpen() {
  const panel = studio.root?.querySelector('[data-studio-sources]')
  if (panel && !panel.hidden) {
    closeSources()
    return true
  }
  return false
}

function mount() {
  const host = document.getElementById('mode-create')
  if (!host) return
  if (studio.initialized && studio.root?.isConnected) return
  studio.initialized = false
  studio.root = null
  host.innerHTML = `<div id="studio-shell-host"></div><div id="legacy-create-shell" hidden></div><button class="studio-exit-l2" type="button" data-studio-exit-l2 hidden>Back to quick start</button>`
  studio.root = host.querySelector('#studio-shell-host')
  studio.root.innerHTML = shellHtml()
  bindShell()
  bindGlobalListeners()
  studio.initialized = true
  studio.popstateHandler = () => {
    if (getTier() !== TIERS.L2) showStudioShell()
  }
  window.addEventListener('popstate', studio.popstateHandler)
  renderAct()
  // Check entry params (e.g. ?entry=sources, ?source=123) after a tick to allow router to set store.mode
  requestAnimationFrame(() => {
    const url = new URL(window.location.href)
    const entry = url.searchParams.get('entry')
    if (entry === 'sources') openEntrySources()
  })
}

export function initStudio() {
  mount()
}

export function activate() {
  // Set act to gallery before mount(), so renderAct() doesn't render hero
  studio.act = 'gallery'
  ensureStudioStageActions()
  mount()
  if (getTier() === TIERS.L2) showLegacyShell()
  else {
    showStudioShell()
    startGallery() // Skip hero, directly start gallery
  }
}

export function deactivate() {
  if (studio.popstateHandler) {
    window.removeEventListener('popstate', studio.popstateHandler)
    studio.popstateHandler = null
  }
  if (studio.escKeydownListener) {
    window.removeEventListener('keydown', studio.escKeydownListener, true)
    studio.escKeydownListener = null
  }
  if (studio.previewModalEscListener) {
    window.removeEventListener('keydown', studio.previewModalEscListener, true)
    studio.previewModalEscListener = null
  }
  // Listener-leak guard: undo every eventBus/store registration from
  // bindGlobalListeners (it re-runs on the next activate).
  studio._unsubs?.forEach((unsub) => unsub())
  studio._unsubs = []
  if (studio.root) studio.root.hidden = true
  studio.initialized = false
  studio.listenersBound = false
  studio.previewModalOpen = false
  // Clean up overlay listener
  document.removeEventListener('click', handleRpOverlayClickOutside, true)
  // Remove studio-focus class
  toggleStudioFocus(false)
}

export function shouldRevealL2(usageCount) {
  return Number(usageCount) >= 5
}

export function getL0DefaultIntent() {
  return { ...L0_DEFAULT_INTENT }
}

export { studio, renderAct, startGallery, openEntrySources, openSources, renderSourceBand, updatePhase }


